import { useEffect, useRef, type RefObject } from 'react';
import { useIntersectionObserver } from './useIntersectionObserver';
import { trackUmamiEvent } from '../utils/umami';

type UmamiEventData = Record<string, string | number | boolean>;

/** Fires a Umami event once when `active` becomes true. */
export const useUmamiEventOnce = (
  event: string,
  active: boolean,
  data?: UmamiEventData
): void => {
  const tracked = useRef(false);
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    if (!active || tracked.current) return;
    tracked.current = true;
    trackUmamiEvent(event, dataRef.current);
  }, [active, event]);
};

/** Stricter than scroll animations — section must actually enter the viewport. */
export const UMAMI_SECTION_VIEW_OPTIONS = {
  threshold: 0.2,
  rootMargin: '0px',
} as const;

/** Fires `section_view` once when the section is meaningfully visible. */
export const useUmamiSectionView = (section: string, visible: boolean): void => {
  useUmamiEventOnce('section_view', visible, { section });
};

/** Own observer for Umami, decoupled from scroll-reveal animations. */
export const useUmamiSectionViewRef = <T extends Element = HTMLElement>(section: string) => {
  const { elementRef, hasBeenVisible } = useIntersectionObserver<T>(UMAMI_SECTION_VIEW_OPTIONS);
  useUmamiSectionView(section, hasBeenVisible);
  return elementRef;
};

/** Fires `landing_scroll` once on the first page or hero-container scroll. */
export const useLandingScroll = (containerRef?: RefObject<HTMLElement | null>): void => {
  const tracked = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      if (tracked.current) return;
      tracked.current = true;
      trackUmamiEvent('landing_scroll');
      window.removeEventListener('scroll', onScroll, true);
      containerRef?.current?.removeEventListener('scroll', onScroll);
    };

    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    const container = containerRef?.current;
    container?.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      container?.removeEventListener('scroll', onScroll);
    };
  }, [containerRef]);
};

const HERO_ENGAGED_MS = 10_000;
const HERO_VIDEO_WATCH_SECONDS = 5;

const heroVideoPlayTracked = { current: false };
const heroVideoWatchedTracked = { current: false };

/** Fires `hero_engaged_10s` once after 10s of visible time on the landing hero. */
export const useHeroEngaged = (durationMs = HERO_ENGAGED_MS): void => {
  const tracked = useRef(false);
  const visibleSeconds = useRef(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (tracked.current || document.visibilityState !== 'visible') return;
      visibleSeconds.current += 1;
      if (visibleSeconds.current * 1000 >= durationMs) {
        tracked.current = true;
        trackUmamiEvent('hero_engaged_10s');
        window.clearInterval(intervalId);
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [durationMs]);
};

/** Shared handlers for hero demo video — fires once across mobile/desktop instances. */
export const useHeroVideoUmamiHandlers = () => {
  const onPlay = () => {
    if (heroVideoPlayTracked.current) return;
    heroVideoPlayTracked.current = true;
    trackUmamiEvent('hero_video_play');
  };

  const onTimeUpdate = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    if (heroVideoWatchedTracked.current) return;
    if (event.currentTarget.currentTime >= HERO_VIDEO_WATCH_SECONDS) {
      heroVideoWatchedTracked.current = true;
      trackUmamiEvent('hero_video_watched', { seconds: HERO_VIDEO_WATCH_SECONDS });
    }
  };

  return { onPlay, onTimeUpdate };
};
